import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { localizeValue } from "../lib/displayValues";
import { Gavel, Clock, TrendingUp, Users, Flame, Zap, ArrowRight, Search, Sparkles } from "lucide-react";

const CATEGORY_FILTERS = [
  { id: "all",   ar: "الكل",     en: "All" },
  { id: "cars",  ar: "سيارات",   en: "Cars" },
  { id: "luxury",ar: "فاخرة",    en: "Luxury" },
  { id: "suv",   ar: "SUV",      en: "SUV" },
  { id: "truck", ar: "شاحنات",   en: "Trucks" },
];

const SORT_OPTIONS = [
  { id: "ending_soon", ar: "الأقرب للانتهاء", en: "Ending soon" },
  { id: "highest_bid", ar: "أعلى مزايدة",     en: "Highest bid" },
  { id: "most_bids",   ar: "الأكثر مزايدات",  en: "Most bids" },
  { id: "newest",      ar: "الأحدث",          en: "Newest" },
];

// ─────────────────────────────────────────────────────────────────────
// Countdown hook — updates every 1s
// ─────────────────────────────────────────────────────────────────────
function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!endsAt) return null;
    const end = new Date(endsAt).getTime();
    const diff = end - now;
    if (diff <= 0) return { ended: true };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    return { days, hours, mins, secs, ended: false };
  }, [now, endsAt]);
}

// ─────────────────────────────────────────────────────────────────────
export default function AuctionsPage() {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("ending_soon");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get("/auctions/live", { params: { limit: 50 } })
      .then(({ data }) => { if (!cancelled) setItems(data.items || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (category !== "all") {
      list = list.filter((v) => v.category === category || v.body?.toLowerCase() === category);
    }
    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      list = list.filter((v) =>
        (v.make || "").toLowerCase().includes(qq) ||
        (v.model || "").toLowerCase().includes(qq) ||
        (v.city || "").toLowerCase().includes(qq)
      );
    }
    const sorted = [...list];
    if (sort === "ending_soon") {
      sorted.sort((a, b) => (a.auction_ends_at || "").localeCompare(b.auction_ends_at || ""));
    } else if (sort === "highest_bid") {
      sorted.sort((a, b) => (Number(b.current_bid_sar || b.starting_bid_sar || 0)) - (Number(a.current_bid_sar || a.starting_bid_sar || 0)));
    } else if (sort === "most_bids") {
      sorted.sort((a, b) => (Number(b.bid_count || 0)) - (Number(a.bid_count || 0)));
    } else if (sort === "newest") {
      sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return sorted;
  }, [items, category, sort, q]);

  const stats = useMemo(() => ({
    live: items.length,
    totalBids: items.reduce((sum, v) => sum + (Number(v.bid_count) || 0), 0),
    totalValue: items.reduce((sum, v) => sum + (Number(v.current_bid_sar || v.starting_bid_sar || 0)), 0),
    endingSoon: items.filter((v) => {
      if (!v.auction_ends_at) return false;
      return (new Date(v.auction_ends_at).getTime() - Date.now()) < 24 * 3600 * 1000;
    }).length,
  }), [items]);

  return (
    <div className="min-h-screen bg-[#faf8f1]" data-testid="auctions-page">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1a0a0a 40%,#3a0a0a 100%)" }}>
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute top-0 start-0 w-96 h-96 rounded-full blur-3xl" style={{ background: "#dc2626" }} />
          <div className="absolute bottom-0 end-0 w-96 h-96 rounded-full blur-3xl" style={{ background: "#c9a55c" }} />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-widest" style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            {lang === "ar" ? "مزادات مباشرة الآن" : "Live auctions now"}
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-6xl lg:text-7xl tracking-[-0.03em] font-semibold leading-[0.98]" style={{ color: "#f7eecd" }}>
            {lang === "ar"
              ? <>مزادات <span style={{ color: "#e8c873" }}>كارتال</span> المباشرة</>
              : <>CARTAL <span style={{ color: "#e8c873" }}>Live</span> Auctions</>}
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg font-medium" style={{ color: "rgba(247,238,205,0.78)" }}>
            {lang === "ar"
              ? "زايد على أفخم السيارات في المملكة — من فاخرة إلى كلاسيكية إلى SUV. أعلى مزايدة تفوز، وضمان كارتال متاح لحماية إضافية."
              : "Bid on the finest vehicles in Saudi Arabia — luxury, classic, SUV, and more. Highest bid wins, with optional CARTAL Escrow protection."}
          </p>
          {/* Stats strip */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl" data-testid="auctions-stats">
            <HeroStat icon={Flame} value={stats.live}       label={lang === "ar" ? "مزاد حي" : "Live auctions"} color="#f87171" />
            <HeroStat icon={TrendingUp} value={stats.totalBids} label={lang === "ar" ? "مزايدة قدّمت" : "Bids placed"} color="#c9a55c" />
            <HeroStat icon={Users} value={stats.endingSoon} label={lang === "ar" ? "ينتهي خلال 24 ساعة" : "Ending within 24h"} color="#fbbf24" />
            <HeroStat icon={Sparkles} value={formatSAR(stats.totalValue)} label={lang === "ar" ? "إجمالي القيمة (ر.س)" : "Total value (SAR)"} color="#34d399" small />
          </div>
        </div>
      </section>

      {/* ── Filters ─────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between mb-6">
          <div className="flex-1 max-w-md relative">
            <Search className="w-4 h-4 absolute top-1/2 -translate-y-1/2 start-3 text-[#8a8a8a]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={lang === "ar" ? "ابحث بالماركة، الموديل، أو المدينة…" : "Search by make, model, or city…"}
              className="w-full ps-9 pe-4 py-2.5 rounded-full text-sm bg-white border border-[#0a0a0a]/10 focus:outline-none focus:border-[#dc2626]/50 focus:ring-2 focus:ring-[#dc2626]/15"
              data-testid="auctions-search"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-full text-sm bg-white border border-[#0a0a0a]/10 px-4 py-2.5 font-medium text-[#0a0a0a]"
            data-testid="auctions-sort"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{lang === "ar" ? o.ar : o.en}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 flex-wrap mb-8" data-testid="auctions-category-filters">
          {CATEGORY_FILTERS.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className="px-4 py-2 rounded-full text-sm font-bold transition-all"
              style={category === c.id
                ? { background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff", border: "1.5px solid #dc2626" }
                : { background: "#fff", color: "#0a0a0a", border: "1.5px solid #e5e5e5" }}
              data-testid={`filter-${c.id}`}
            >
              {lang === "ar" ? c.ar : c.en}
            </button>
          ))}
        </div>

        {/* ── Grid ─────────────────────────────────────────── */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={`skeleton-${i}`} className="h-96 rounded-3xl bg-white/60 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState lang={lang} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="auctions-grid">
            {filtered.map((v) => <AuctionCard key={v.id} v={v} lang={lang} />)}
          </div>
        )}
      </section>

      {/* ── Bottom CTA ───────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pb-16">
        <div
          className="rounded-3xl p-8 sm:p-12 relative overflow-hidden text-center"
          style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1f1a10 50%,#3d2e10 100%)", border: "2px solid #c9a55c" }}
        >
          <div className="absolute -bottom-16 start-1/2 -translate-x-1/2 w-80 h-80 rounded-full opacity-20 blur-3xl" style={{ background: "#dc2626" }} />
          <div className="relative">
            <div className="inline-flex items-center gap-2 overline mb-2" style={{ color: "#fca5a5" }}>
              <Gavel className="w-3 h-3" /> {lang === "ar" ? "بائعون — انضموا" : "Sellers — join in"}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold" style={{ color: "#f7eecd" }}>
              {lang === "ar" ? "أدرج سيارتك في مزاد — دع السوق يحدد السعر" : "List your car for auction — let the market set the price"}
            </h2>
            <p className="text-sm mt-3 max-w-2xl mx-auto" style={{ color: "rgba(247,238,205,0.75)" }}>
              {lang === "ar"
                ? "حدد سعر البدء والاحتياطي و\"اشترِ الآن\" — نتولى الباقي. الفائز يُشعَر تلقائياً ولديه 24 ساعة لإتمام الدفع."
                : "Set your starting bid, reserve, and buy-now — we handle the rest. Winner is auto-notified with 24h to settle."}
            </p>
            <Link
              to="/sell"
              className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full font-bold text-sm shadow-xl transition-transform hover:scale-105"
              style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}
              data-testid="auctions-cta-sell"
            >
              <Zap className="w-4 h-4" />
              {lang === "ar" ? "ابدأ مزاداً الآن" : "Start an auction now"}
              <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function HeroStat({ icon: Icon, value, label, color, small }) {
  return (
    <div className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div className="inline-flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "rgba(247,238,205,0.65)" }}>{label}</span>
      </div>
      <div className={`font-display font-bold ${small ? "text-lg" : "text-2xl"}`} style={{ color: "#f7eecd" }}>{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
function AuctionCard({ v, lang }) {
  const t = useCountdown(v.auction_ends_at);
  const tr = (s) => localizeValue(s, lang);
  const current = Number(v.current_bid_sar || 0);
  const starting = Number(v.starting_bid_sar || v.price_sar || 0);
  const bidCount = Number(v.bid_count || 0);
  const endingSoon = t && !t.ended && t.days === 0 && t.hours < 24;

  return (
    <Link
      to={`/vehicle/${v.id}`}
      className="group block rounded-3xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-2xl"
      style={{ background: "#fff", border: "1.5px solid rgba(220,38,38,0.25)" }}
      data-testid={`auction-card-${v.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={v.image || v.images?.[0]}
          alt={`${v.make} ${v.model}`}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* Live badge */}
        <div className="absolute top-3 start-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-md" style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}>
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          {lang === "ar" ? "مباشر" : "Live"}
        </div>
        {/* Countdown pill */}
        {t && !t.ended && (
          <div
            className="absolute top-3 end-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold shadow-md"
            style={{
              background: endingSoon ? "linear-gradient(135deg,#fbbf24,#f59e0b)" : "rgba(10,10,10,0.85)",
              color: endingSoon ? "#0a0a0a" : "#e8c873",
              border: endingSoon ? "1px solid #f59e0b" : "1px solid #c9a55c",
            }}
          >
            <Clock className="w-3 h-3" />
            {t.days > 0 && <>{t.days}{lang === "ar" ? "ي" : "d"} </>}
            {String(t.hours).padStart(2, "0")}:{String(t.mins).padStart(2, "0")}:{String(t.secs).padStart(2, "0")}
          </div>
        )}
        {/* Bid count */}
        {bidCount > 0 && (
          <div className="absolute bottom-3 start-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold shadow-md bg-white/95 text-[#0a0a0a] border border-[#0a0a0a]/10">
            <TrendingUp className="w-3 h-3 text-[#dc2626]" />
            {bidCount} {lang === "ar" ? (bidCount === 1 ? "مزايدة" : "مزايدات") : (bidCount === 1 ? "bid" : "bids")}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="text-xs text-[#8a8a8a] font-medium">{v.year} · {v.make} · {tr(v.city)}</div>
        <h3 className="font-display text-xl font-bold text-[#0a0a0a] mt-0.5 truncate group-hover:text-[#dc2626] transition-colors">
          {v.model}
        </h3>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#dc2626]">
              {current > 0 ? (lang === "ar" ? "أعلى مزايدة" : "Current bid") : (lang === "ar" ? "سعر البدء" : "Starting")}
            </div>
            <div className="font-display text-2xl font-bold text-[#0a0a0a]">
              {formatSAR(current || starting)} <span className="text-xs font-medium text-[#8a8a8a]">SAR</span>
            </div>
          </div>
          <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold transition-transform group-hover:scale-105"
            style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}>
            {lang === "ar" ? "زايد الآن" : "Bid now"}
            <ArrowRight className="w-3.5 h-3.5 rtl:rotate-180" />
          </div>
        </div>
      </div>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────
function EmptyState({ lang }) {
  return (
    <div className="rounded-3xl bg-white p-12 text-center border border-[#0a0a0a]/10" data-testid="auctions-empty">
      <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(220,38,38,0.1)", border: "1.5px solid rgba(220,38,38,0.3)" }}>
        <Gavel className="w-7 h-7 text-[#dc2626]" />
      </div>
      <h3 className="font-display text-2xl font-semibold text-[#0a0a0a]">
        {lang === "ar" ? "لا مزادات حية حالياً" : "No live auctions right now"}
      </h3>
      <p className="text-sm text-[#525252] mt-2 max-w-md mx-auto">
        {lang === "ar"
          ? "كن أول من يبدأ مزاداً على منصة كارتال — احصل على أفضل سعر عبر منافسة الجمهور."
          : "Be the first to start an auction on CARTAL — get the best price through public competition."}
      </p>
      <Link
        to="/sell"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm"
        style={{ background: "linear-gradient(135deg,#dc2626,#9f1239)", color: "#fff" }}
      >
        <Zap className="w-4 h-4" />
        {lang === "ar" ? "ابدأ مزادك الآن" : "Start your auction"}
      </Link>
    </div>
  );
}
